import React from 'react'
import { useTheme } from '@mui/material/styles'
import { DataGridPro, gridClasses, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { getMuiDataGridLocaleText } from '../../../i18n/localeUtils'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import * as datefns from 'date-fns'
import { CustomDataGridToolbar } from '../../../components/custom-grid-components/CustomDataGridToolbar'
import { CustomerListItem } from '@finance-lab/utils/types'

const customersColumns = (): Array<GridColDef<CustomerListItem>> => [
    {
        field: 'name',
        headerName: 'Namn',
        minWidth: 150,
        flex: 1,
        renderCell: ({ row: customer, value: name }) => {
            return <Link to={`/admin/customers/${customer.id}`}>{name}</Link>
        },
    },
    {
        field: 'userCount',
        headerName: 'Aktiverade användare (totalt)',
        minWidth: 150,
        flex: 1,
        valueGetter: (_, customer) => {
            return customer.totalUserCount - customer.disabledUserCount
        },
        renderCell: ({ row: customer }) => {
            return `${customer.totalUserCount - customer.disabledUserCount} (${customer.totalUserCount})`
        },
    },
    {
        field: 'lastActive',
        headerName: 'Senast aktiv',
        minWidth: 150,
        flex: 1,
        valueFormatter: (value) => (value ? datefns.format(new Date(value), 'yyyy-MM-dd HH:mm:ss') : ''),
    },
]

function CustomersGrid({
    customers,
    loading,
    paginationMode = 'client',
    paginationModel,
    onPaginationModelChange,
    rowCount,
    sortingMode = 'client',
    sortModel,
    onSortModelChange,
}: {
    customers: CustomerListItem[]
    loading?: boolean
    paginationMode?: 'client' | 'server'
    paginationModel?: GridPaginationModel
    onPaginationModelChange?: (model: GridPaginationModel) => void
    rowCount?: number
    sortingMode?: 'client' | 'server'
    sortModel?: GridSortModel
    onSortModelChange?: (model: GridSortModel) => void
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }
    const theme = useTheme()

    return (
        <div style={{ width: '100%' }}>
            <DataGridPro
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main,
                    },
                    '& .odd': {},
                }}
                rows={customers}
                columns={customersColumns()}
                localeText={getMuiDataGridLocaleText(locale)}
                slots={{ toolbar: CustomDataGridToolbar }}
                loading={loading}
                pagination={paginationMode === 'server'}
                paginationMode={paginationMode}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                rowCount={rowCount}
                pageSizeOptions={[25, 50, 100]}
                sortingMode={sortingMode}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton',
                    },
                }}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                disableColumnFilter
                disableColumnMenu
                disableRowSelectionOnClick
            />
        </div>
    )
}

export default CustomersGrid
